
import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { TestDetails } from "@/hooks/useTestCreator";
import { Question } from "@/types/test";

// Helper for exponential backoff retry
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const usePendingTestCreation = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const processingRef = useRef(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Function to create test with retry logic
  const createTest = async (
    userId: string, 
    testDetails: TestDetails, 
    questions: Question[],
    shouldPublish: boolean,
    maxRetries = 3
  ): Promise<string | null> => {
    let retryCount = 0;
    let lastError: any = null;
    
    while (retryCount <= maxRetries) {
      try {
        // Generate a share token using crypto API
        const shareToken = crypto.randomUUID();
        
        // Prepare the test data with the user's ID
        const testData = {
          title: testDetails.title || 'Untitled Test',
          description: testDetails.description || 'No description provided',
          creator_id: userId,
          key_responsibilities: testDetails.keyResponsibilities || '',
          years_of_experience: testDetails.yearsOfExperience || '',
          location: testDetails.location || 'Remote',
          language: "English",
          key_skills: Array.isArray(testDetails.keySkills) ? testDetails.keySkills : ['General'],
          time_limit: testDetails.timeLimit || 60,
          status: shouldPublish ? 'published' : 'draft',
          publish_to_job_board: testDetails.publishToJobBoard || false,
          work_arrangement: testDetails.workArrangement || 'Full-Time',
          salary_range_min: testDetails.salaryRangeMin || null,
          salary_range_max: testDetails.salaryRangeMax || null,
          salary_currency: testDetails.salaryCurrency || 'INR',
          share_token: shareToken,
          is_active: true,
          time_limit_type: 'per_question',
          work_presence: testDetails.workPresence || 'Fully On-site',
          industry: testDetails.industry || null,
          seniority: testDetails.seniority || null,
          total_time_limit: testDetails.timeLimit || 60
        };

        // Save the test to the database
        const { data: testResponse, error: testError } = await supabase
          .from("tests")
          .insert(testData)
          .select()
          .single();
      
        if (testError) {
          throw testError;
        }
        
        if (!testResponse || !testResponse.id) {
          throw new Error('Failed to create test: No test ID returned');
        }
        
        // Save questions with order field
        const questionsData = questions.map((q, index) => ({
          test_id: testResponse.id,
          question_type: q.question_type,
          content: q.content,
          time_limit: q.time_limit || 60,
          marks: q.marks || 1,
          order: index,
          code_tests: q.code_tests
        }));
        
        const { error: questionsError } = await supabase
          .from("questions")
          .insert(questionsData);

        if (questionsError) {
          throw questionsError;
        }
        
        return testResponse.id;
      } catch (err: any) {
        lastError = err;
        console.warn(`[usePendingTestCreation] Attempt ${retryCount + 1} failed:`, err);
        
        if (err.code === '42501' || // permission denied error
            err.message.includes('policy') || 
            err.message.includes('permission')) {
          // This is likely an RLS policy error due to race condition
          // Wait with exponential backoff before trying again
          const waitTime = Math.pow(2, retryCount) * 1000 + Math.random() * 1000;
          console.log(`[usePendingTestCreation] Waiting ${waitTime}ms before retry`);
          await delay(waitTime);
          retryCount++;
        } else {
          // For other types of errors, stop retrying
          break;
        }
      }
    }
    
    // If we got here, all retries failed
    throw lastError || new Error('Failed to create test after multiple attempts');
  };

  const processPendingTest = useCallback(async (userId: string): Promise<boolean> => {
    // Prevent duplicate processing
    if (processingRef.current || isCreating) {
      console.log("[usePendingTestCreation] Already processing a test, skipping");
      return false;
    }

    // Set processing flags
    processingRef.current = true;
    setIsCreating(true);
    setError(null);
    
    try {
      // Get pending test data
      const pendingTestDetails = sessionStorage.getItem('pendingTestDetails');
      const pendingTestQuestions = sessionStorage.getItem('pendingTestQuestions');
      const shouldPublishTest = sessionStorage.getItem('shouldPublishTest');
      const pendingRedirectPath = sessionStorage.getItem('pendingRedirectPath') || '/test-management';
      
      // If no pending test data, just return false
      if (!pendingTestDetails || !pendingTestQuestions) {
        console.log("[usePendingTestCreation] No pending test data found");
        return false;
      }
      
      console.log("[usePendingTestCreation] Found pending test data to save for user:", userId);
      
      // Clear session storage BEFORE creating test to prevent duplicate creation
      sessionStorage.removeItem('pendingTestDetails');
      sessionStorage.removeItem('pendingTestQuestions');
      sessionStorage.removeItem('shouldPublishTest');
      sessionStorage.removeItem('isCreatingTest');
      sessionStorage.removeItem('pendingRedirectPath');
      
      // Parse the stored test details and questions
      const testDetails: TestDetails = JSON.parse(pendingTestDetails);
      const questions: Question[] = JSON.parse(pendingTestQuestions);

      // Initial delay to ensure profile with role is established
      console.log("[usePendingTestCreation] Adding initial delay to ensure profile is created");
      await delay(1500);
      
      // REMOVED: Call to update-subscription edge function
      // The database trigger will handle this automatically
      
      // Create the test with retry logic
      const testId = await createTest(
        userId,
        testDetails,
        questions,
        shouldPublishTest === 'true',
        3 // max retries
      );
      
      if (!testId) {
        throw new Error('Failed to create test: No test ID returned');
      }
      
      toast({
        title: shouldPublishTest === 'true' ? "Test Published!" : "Test Saved",
        description: shouldPublishTest === 'true' 
          ? "Your test is now available for candidates" 
          : "Your test has been saved as a draft",
      });
      
      // Navigate to success screen directly with the test ID for published tests
      if (shouldPublishTest === 'true') {
        navigate(`/test-creator/success/${testId}`, { replace: true });
      } else {
        // For drafts, go to test management
        navigate(pendingRedirectPath, { replace: true });
      }
      
      return true;
    } catch (error: any) {
      console.error('[usePendingTestCreation] Error processing pending test:', error);
      console.error('[usePendingTestCreation] Error stack:', error.stack);
      setError(error.message || 'Failed to create test');
      
      toast({
        title: "Error",
        description: error.message || "Failed to save your test. Please try again.",
        variant: "destructive",
      });
      
      return false;
    } finally {
      setIsCreating(false);
      processingRef.current = false;
    }
  }, [navigate, toast, isCreating]);
  
  return {
    processPendingTest,
    isCreating,
    error
  };
};
